"use client";

/**
 * Client-side providers — Theme + React Query + Auth context.
 * Wrapped around the app in the root layout.
 */

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { GlobalLoadingBar } from "@/components/ui/global-loading-bar";
import { AuthProvider } from "@/lib/auth/context";
import { ThemeProvider } from "@/lib/theme/context";
import { NotificationProvider } from "@/lib/notifications/context";
import { NotificationRenderer } from "@/components/ui/notifications";
import {
  ObservabilityBridge,
  ObservabilityErrorBoundary,
  ObservabilityProvider,
} from "@/lib/observability";
import { TimezoneProvider } from "@/lib/time";

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // EINE MINUTE WAR DIE DAUER EINER VORFUEHRUNG, NICHT DIE DER
            // DATEN. Das Lager schreibt einmal am Tag; alles, was ein Kunde
            // zu sehen bekommt, ist waehrend eines Termins unveraenderlich.
            // Mit 60s lief jeder Sprung zwischen zwei Seiten, der laenger als
            // eine Minute auseinanderlag, in einen vollstaendigen Neuabruf:
            // auf My Dashboard sind das 31 Abfragen fuer Zahlen, die sich
            // nachweislich nicht bewegt haben.
            staleTime: 15 * 60 * 1000,
            // gcTime ist der zweite, unauffaelligere Teil davon. Der Standard
            // wirft den Eintrag 5 Minuten nach dem Ausmounten weg, also genau
            // dann, wenn jemand eine Seite laenger erklaert und zurueckgeht -
            // und dann half auch das laengste staleTime nichts mehr, weil es
            // nichts mehr gab, das frisch sein koennte.
            gcTime: 60 * 60 * 1000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {/* Directly under the QueryClientProvider, because that is the
            state it reads, and above every other provider so it is on
            screen for pre-auth fetches too. It renders nothing until a
            request has been outstanding long enough to be worth saying
            so — see the component. */}
        <GlobalLoadingBar />
        {/*
          ObservabilityProvider sits above NotificationProvider so the
          PostHog browser SDK is ready before any auth-dependent code
          runs (including pre-auth pageviews on /login). Mounted
          unconditionally — children always get a context, with
          ``posthog`` null whenever the SDK is disabled via env.

          ObservabilityBridge sits *below* AuthProvider so it can read
          ``useAuth()`` and drive identify / group / opt-in / opt-out
          off auth state changes. It is a passthrough wrapper.

          ObservabilityErrorBoundary sits at the INNERMOST level so it
          catches errors in the actual page tree without swallowing
          provider-init errors (which leave the app non-functional
          anyway). NotificationProvider / AuthProvider / TimezoneProvider
          stay intact above it so the fallback can still render toasts
          + read auth context if needed.

          See plans/PLAN_POSTHOG_01_FOUNDATIONS.md § "Provider
          architecture" + plans/PLAN_POSTHOG_03_OBSERVABILITY.md
          § "ErrorBoundary".
        */}
        <ObservabilityProvider>
          <NotificationProvider>
            <AuthProvider>
              <ObservabilityBridge>
                <TimezoneProvider>
                  <ObservabilityErrorBoundary>
                    {children}
                  </ObservabilityErrorBoundary>
                </TimezoneProvider>
              </ObservabilityBridge>
            </AuthProvider>
            <NotificationRenderer />
          </NotificationProvider>
        </ObservabilityProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
